/*
 * IMPORTS
 */
import React from "react";
import Chart from "react-apexcharts";
import _ from "underscore";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useSelector } from "react-redux";


/*
 * PACKAGES
 */
import Card from 'components/Card'


/*
 * GLOBALS
 * Maximum number of data points to keep in chart
 * to prevent memory overflow on long running sessions.
 */
const _MAX_DATA_POINTS = 20


/*
 * COMPONENT
 */
const Index = ({ title }) => {
  /*
   * Memoized chart options to prevent
   * unnecessary re-creation on every render.
   */
  const $options = React.useMemo(
    () => ({
      chart: {
        type: "line",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 450,
          },
        },
        sparkline: {
          enabled: true,
        },
        toolbar: {
          show: false,
        },
        dropShadow: {
          enabled: true,
          top: 13,
          left: 0,
          blur: 10,
          opacity: 0.1,
          color: "#3B2CF3",
        },
      },
      xaxis: {
        labels: {
          show: false,
          style: {
            colors: "#3B2CF3",
          },
        },
        axisBorder: {
          show: true,
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        title: {
          show: false,
        },
        labels: {
          style: {
            colors: "#3B2CF3",
          },
          show: false,
        },
      },
      colors: ["#A493F5", "#FF6B6B"],
      markers: {
        size: 0,
        colors: "white",
        strokeColors: "#3B2CF3",
        strokeWidth: 0,
        strokeOpacity: 0.9,
        strokeDashArray: 0,
        fillOpacity: 0,
        discrete: [],
        shape: "circle",
        radius: 0,
        offsetX: 0,
        offsetY: 0,
        showNullDataPoints: true,
      },
      tooltip: {
        theme: "dark",
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        type: "gradient",
        width: 2,
      },
      dropShadow: {
        enabled: true,
        top: 0,
        left: 0,
        blur: 5,
        opacity: 0.5,
      },
      grid: {
        show: false,
        column: {
          colors: ["transparent"],
          opacity: 0.5,
        },
      },
    }),
    [],
  ); // Empty dependency — options never change.

  /*
   * Single state object for both CPU and RAM data.
   * Combined to ensure only 1 re-render per update
   * instead of 2 separate re-renders.
   */
  const [chartData, setChartData] = React.useState({
    cpu: [0],
    ram: [0],
  });

  /*
   * Get DB server data from Redux store.
   * Using remote state as this tracks DB server metrics.
   */
  const { data: nodeServerData } = useSelector(
    (state) => state.SystemInfo.remote,
  );

  /*
   * Effect to process and update chart data
   * whenever new server metrics are received.
   */
  React.useEffect(() => {
    // Skip update if data is empty or null.
    if (_.isEmpty(nodeServerData)) return;

    // Extract CPU usage percentage from server data.
    const currentLoad =
      parseFloat(nodeServerData?.cpu?.usage_percent ?? 0) || 0;

    // Calculate RAM usage percentage from used/total values.
    const ramUsed = parseFloat(nodeServerData?.memory?.used ?? 0) || 0;
    const ramTotal = parseFloat(nodeServerData?.memory?.total ?? 1) || 1;
    const ramPercent = parseFloat(((ramUsed / ramTotal) * 100).toFixed(2));

    /*
     * Use startTransition to mark this update as
     * low priority so UI interactions stay responsive.
     * Single setState call ensures only 1 re-render.
     */
    React.startTransition(() => {
      setChartData((prev) => ({
        cpu: [...prev.cpu, currentLoad].slice(-_MAX_DATA_POINTS),
        ram: [...prev.ram, ramPercent].slice(-_MAX_DATA_POINTS),
      }));
    });
  }, [nodeServerData]); // Only re-run when server data changes.

  /*
   * Memoized series data to prevent chart from
   * rebuilding unless cpu or ram data actually changes.
   */
  const $series = React.useMemo(
    () => [
      {
        name: "CPU Usage",
        data: chartData.cpu,
      },
      {
        name: "RAM Usage",
        data: chartData.ram,
      },
    ],
    [chartData],
  );

  // Return component.
  return (
    <Card
      minH="420px"
      h="100%"
      align="center"
      direction="column"
      bg="linear-gradient(23deg, #BCC0FF 0%, #FFDBE7 45.69%, #FFEED4 99.80%)"
      p="0"
      boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.17)"
    >
      <Flex justify="space-between" align="start" pt="5px">
        <Flex flexDirection="column" align="start" me="20px">
          <Flex w="100%" p="20px" flexDir="column" justifyContent="flex-start">
            <Text
              color="secondaryGray.900"
              fontWeight="400"
              fontSize="24px"
              textAlign="left"
              lineHeight="28px"
            >
              {title || "DB Server CPU & RAM Usage"}
            </Text>
            <Text color="secondaryGray.900" fontWeight="700" fontSize="16px">
              Live monitoring of server load in %
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Box
        h="100%"
        mt="auto"
        overflow="hidden"
        borderBottomLeftRadius={22}
        borderBottomRightRadius={22}
      >
        <Chart
          options={$options}
          series={$series}
          type="line"
          width="100%"
          height="100%"
          redrawOnParentResize={true}
        />
      </Box>
    </Card>
  );
};


/*
 * EXPORTS
 */
export default Index;